/**
 * TalentCompetency MVP — Profile Catalog (Sprint TCO-A).
 *
 * Lists all Corporate Talent Hub global Competency Profiles. Profiles link to
 * PUBLISHED Evaluation Library questionnaires only. Questionnaire creation
 * is the exclusive responsibility of Assessment Studio + Evaluation Library;
 * it is NEVER performed inside TalentCompetency.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Layers, Search, Archive as ArchiveIcon, Eye, Inbox } from 'lucide-react';

import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import { competencyProfileApi, competencyRequestAdminApi } from '@/lib/competencyProfileApi';
import { formatApiError } from '@/lib/api';
import { PendingJobProfileRequests } from '@/components/admin/PendingJobProfileRequests';
import SuiteOpsPanel from '@/components/admin/SuiteOpsPanel';

const STATUS_TONES = {
  draft:     'bg-slate-100 text-slate-700 border-slate-200',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  approved:  'bg-blue-50 text-blue-700 border-blue-200',
  published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived:  'bg-zinc-100 text-zinc-500 border-zinc-200',
};

const STATUSES = ['draft', 'submitted', 'approved', 'published', 'archived'];

export default function AdminTalentCompetency() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', department: '', description: '' });
  // Founder UAT iter 3 — opened from a client request: link the new profile
  // so publishing it auto-completes the request as "profile_available".
  const [searchParams] = useSearchParams();
  const fromRequestId = searchParams.get('fromRequest') || null;
  useEffect(() => {
    if (!fromRequestId) return;
    // Founder UAT sprint (FIX 1/2) — if this request already has a linked
    // profile, RESUME it. Never restart the creation flow from zero.
    competencyRequestAdminApi.get(fromRequestId).then((req) => {
      if (req.linked_profile?.id) {
        toast.info(t('tco.resumingLinkedProfile', {
          defaultValue: 'Esta solicitud ya tiene un perfil vinculado — continuando el trabajo guardado.',
        }));
        navigate(`/admin/talentcompetency/profiles/${req.linked_profile.id}`, { replace: true });
        return;
      }
      setDraft((d) => ({
        name: d.name || req.proposed_title || searchParams.get('title') || '',
        department: d.department || req.proposed_department || '',
        description: d.description || req.business_justification || '',
      }));
      setCreateOpen(true);
    }).catch(() => {
      setDraft((d) => ({ ...d, name: searchParams.get('title') || d.name }));
      setCreateOpen(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromRequestId]);

  const reload = async () => {
    setLoading(true);
    try {
      const rows = await competencyProfileApi.list();
      setProfiles(rows);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return profiles
      .filter((p) => statusFilter === 'all' || p.status === statusFilter)
      .filter((p) => !q
        || (p.name || '').toLowerCase().includes(q)
        || (p.department || '').toLowerCase().includes(q));
  }, [profiles, statusFilter, search]);

  const counts = useMemo(() => {
    const map = { all: profiles.length };
    STATUSES.forEach((s) => { map[s] = profiles.filter((p) => p.status === s).length; });
    return map;
  }, [profiles]);

  const handleCreate = async () => {
    const name = (draft.name || '').trim();
    if (!name) {
      toast.error(t('tco.errors.nameRequired', { defaultValue: 'Profile name is required.' }));
      return;
    }
    setCreating(true);
    try {
      const created = await competencyProfileApi.create({
        name,
        department: draft.department.trim() || null,
        description: draft.description.trim() || null,
        competencies: [],
        linked_questionnaire_ids: [],
        source_request_id: fromRequestId,
      });
      toast.success(t('tco.created', { defaultValue: 'Profile created' }));
      setCreateOpen(false);
      setDraft({ name: '', department: '', description: '' });
      navigate(`/admin/talentcompetency/profiles/${created.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Topbar
        title="TalentCompetency"
        subtitle={t('tco.subtitle', {
          defaultValue: 'Global CTH competency profiles linked to the published Evaluation Library.',
        })}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/talentcompetency/requests')}
              data-testid="tco-open-requests-btn"
            >
              <Inbox className="h-4 w-4 mr-1.5" />
              {t('tco.requestsInbox', { defaultValue: 'Requests' })}
            </Button>
            <Button
              onClick={() => setCreateOpen(true)}
              data-testid="tco-new-profile-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              {t('tco.newProfile', { defaultValue: 'New profile' })}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="tco-catalog">
        {/* UAT A2/A4 — pending Job Profile creation work is visible on the
            suite workspace itself, not only via notification deep-links. */}
        <PendingJobProfileRequests />
        {/* Founder UAT — competency operations supervision layer (role-aware) */}
        <SuiteOpsPanel suiteCode="talentcompetency" />
        <Card className="p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Layers className="h-4 w-4" />
              {t('tco.catalogHeader', {
                defaultValue: 'Competency Profile catalog · {{count}} profiles',
                count: counts.all,
              })}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8 h-9 w-64"
                  placeholder={t('tco.searchPh', { defaultValue: 'Search profiles…' })}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="tco-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-44" data-testid="tco-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="tco-status-all">
                    {t('tco.statusAll', { defaultValue: 'All statuses' })} ({counts.all})
                  </SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`tco-status-${s}`}>
                      {t(`tco.status.${s}`, { defaultValue: s })} ({counts[s] || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          {loading ? (
            <div className="p-8 text-sm text-muted-foreground" data-testid="tco-loading">
              {t('common.loading', { defaultValue: 'Loading…' })}
            </div>
          ) : visible.length === 0 ? (
            <div className="p-12 text-center" data-testid="tco-empty">
              <ArchiveIcon className="h-10 w-10 mx-auto text-muted-foreground/60 mb-3" />
              <p className="text-sm text-muted-foreground">
                {profiles.length === 0
                  ? t('tco.emptyAll', { defaultValue: 'No competency profiles yet. Create your first one.' })
                  : t('tco.emptyFiltered', { defaultValue: 'No profiles match the current filters.' })}
              </p>
            </div>
          ) : (
            <div className="divide-y" data-testid="tco-profile-list">
              {visible.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => navigate(`/admin/talentcompetency/profiles/${p.id}`)}
                  className="w-full text-left px-5 py-4 hover:bg-accent/40 transition flex items-start gap-4"
                  data-testid={`tco-profile-row-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-medium text-foreground truncate" data-testid={`tco-profile-name-${p.id}`}>
                        {p.name}
                      </h3>
                      <Badge
                        variant="outline"
                        className={`text-xs ${STATUS_TONES[p.status] || ''}`}
                        data-testid={`tco-profile-status-${p.id}`}
                      >
                        {t(`tco.status.${p.status}`, { defaultValue: p.status })}
                      </Badge>
                      {p.department && (
                        <span className="text-xs text-muted-foreground" data-testid={`tco-profile-dept-${p.id}`}>
                          {p.department}
                        </span>
                      )}
                    </div>
                    {p.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
                    )}
                    <div className="text-xs text-muted-foreground mt-2 flex items-center gap-4 flex-wrap">
                      <span>
                        {t('tco.competenciesCount', { defaultValue: '{{n}} competencies', n: (p.competencies || []).length })}
                      </span>
                      <span>
                        {t('tco.linkedQsCount', { defaultValue: '{{n}} linked assessments', n: (p.linked_questionnaire_ids || []).length })}
                      </span>
                      {/* FIX 2 — extra identification metadata for same-named profiles */}
                      {p.assigned_advisor_name && (
                        <span data-testid={`tco-profile-advisor-${p.id}`}>{p.assigned_advisor_name}</span>
                      )}
                      {p.updated_at && (
                        <span data-testid={`tco-profile-updated-${p.id}`}>
                          {t('tco.updatedAt', { defaultValue: 'Actualizado' })}: {String(p.updated_at).slice(0, 10)}
                        </span>
                      )}
                    </div>
                  </div>
                  <Eye className="h-4 w-4 text-muted-foreground self-center" />
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="tco-create-dialog">
          <DialogHeader>
            <DialogTitle>{t('tco.createTitle', { defaultValue: 'New competency profile' })}</DialogTitle>
            <DialogDescription>
              {t('tco.createDescription', {
                defaultValue: 'Profiles are global CTH templates. You can link them to published Evaluation Library assessments in the editor.',
              })}
            </DialogDescription>
          </DialogHeader>

          {fromRequestId && (
            <div className="rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2 text-xs text-sky-900" data-testid="tco-create-linked-hint">
              {t('tco.linkedToRequestV2', {
                defaultValue: 'Estás creando un PERFIL DE COMPETENCIAS para el puesto solicitado por el cliente. Título, departamento y justificación se pre-cargaron desde la solicitud; ajústalos si es necesario. Al PUBLICAR el perfil, la solicitud se completará automáticamente como “Perfil disponible”.',
              })}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="tco-create-name">
                {t('tco.fields.name', { defaultValue: 'Profile name' })} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="tco-create-name"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Senior Accountant"
                data-testid="tco-create-name-input"
              />
            </div>
            <div>
              <Label htmlFor="tco-create-dept">{t('tco.fields.department', { defaultValue: 'Department' })}</Label>
              <Input
                id="tco-create-dept"
                value={draft.department}
                onChange={(e) => setDraft((d) => ({ ...d, department: e.target.value }))}
                placeholder="e.g. Finance"
                data-testid="tco-create-dept-input"
              />
            </div>
            <div>
              <Label htmlFor="tco-create-desc">{t('tco.fields.description', { defaultValue: 'Description' })}</Label>
              <Textarea
                id="tco-create-desc"
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                rows={3}
                placeholder={t('tco.fields.descriptionPh', { defaultValue: 'Short description of the role this profile evaluates.' })}
                data-testid="tco-create-desc-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="tco-create-cancel-btn">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="tco-create-submit-btn">
              {creating
                ? t('common.creating', { defaultValue: 'Creating…' })
                : t('tco.create', { defaultValue: 'Create profile' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
